import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import useGet from '../Hooks/useGet';
import API_ENDPOINTS from '../Config/api';
import ClipLoader from 'react-spinners/ClipLoader';
import { Icon } from '../Components/UI/Icon';
import HeaderBanner from '../Components/HeaderBanner';
import { toast } from 'react-toastify';

export default function QuestionarioView() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [exporting, setExporting] = useState(false);

  // Recupera tutte le risposte del questionario
  const { fetchedData: risposteData } = useGet(
    API_ENDPOINTS.questionarioRisposte(id)
  );

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.questionarioRisposte(id).replace('/risposte/', '/export-pdf/')}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem('access_token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Errore durante l\'esportazione del PDF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `questionario_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(t('questionario.exportSuccess', 'PDF esportato con successo'));
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error(t('questionario.exportError', 'Errore durante l\'esportazione del PDF'));
    } finally {
      setExporting(false);
    }
  };
  if (!risposteData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <HeaderBanner 
          title={t('Compliance.title')} 
          linkTo='/compliance' 
          description={t('questionario.viewDescription', 'Visualizza il questionario compilato')} 
        />
        <div className="flex justify-center items-center h-96">
          <div className="text-center">
            <ClipLoader size={50} color="#3466b6" />
            <p className="mt-4 text-gray-600">{t('questionario.loading', 'Caricamento questionario...')}</p>
          </div>
        </div>
      </div>
    );
  }

  const sezioni = risposteData?.sezioni || {};

  return (
    <div>
      <HeaderBanner 
        title={t('Compliance.title')} 
        linkTo='/compliance' 
        description={t('questionario.viewDescription', 'Visualizza il questionario compilato')} 
      />
      
      <div className="mx-auto max-w-7xl mt-5 mb-10">
        {/* Tutte le sezioni in una pagina */}
        {Object.entries(sezioni).map(([sectionName, sezione]) => (
          <div key={sectionName} className="border p-4 mb-6 flex flex-col rounded-md bg-white">
            <h2 className="text-2xl font-bold mb-4 text-black border-b pb-2">{t(`nis2Categories.${sectionName}`, '') || sectionName}</h2>
            
            {sezione.risposte && Array.isArray(sezione.risposte) && sezione.risposte.length > 0 ? (
              <div className="space-y-2">
                {sezione.risposte.map((domanda, index) => {
                  if (!domanda || !domanda.domanda_id) return null;

                  return (
                    <div 
                      key={domanda.domanda_id} 
                      className={`p-3 rounded ${index % 2 !== 0 ? 'bg-gray-50' : 'bg-white'}`}
                    >
                      <div className="flex items-center justify-between gap-4">
                        <p className="font-medium text-black flex-1">{domanda.domanda_testo}</p>
                        
                        <div className="flex-shrink-0">
                          {(domanda.domanda_tipo === 'libera' || domanda.domanda_tipo === 'testo') ? (
                            <div className="p-2 bg-gray-100 rounded border min-w-[200px]">
                              <p className="text-gray-700 text-sm">{domanda.testo_risposta || '-'}</p>
                            </div>
                          ) : domanda.opzione_selezionata ? (
                            <span className={`px-4 py-2 rounded font-semibold text-sm ${
                              domanda.opzione_selezionata.testo?.toUpperCase() === 'SI' 
                                ? 'bg-green-100 text-green-800' 
                                : domanda.opzione_selezionata.testo?.toUpperCase() === 'PARZIALMENTE'
                                ? 'bg-yellow-100 text-yellow-800'
                                : domanda.opzione_selezionata.testo?.toUpperCase() === 'NO'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {domanda.opzione_selezionata.testo}
                            </span>
                          ) : (
                            <span className="text-gray-500 italic text-sm">{t('questionario.noAnswer', 'Nessuna risposta')}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500 italic">{t('questionario.noQuestions', 'Nessuna domanda in questa sezione')}</p>
            )}
          </div>
        ))}

        {/* Bottone Esporta PDF */}
        <div className="flex justify-center mt-8">
          <button
            onClick={handleExportPDF}
            disabled={exporting}
            className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3 text-lg font-semibold shadow-lg"
          >
            {exporting ? (
              <>
                <ClipLoader size={20} color="#ffffff" />
                <span>{t('questionario.exporting', 'Esportazione in corso...')}</span>
              </>
            ) : (
              <>
                <svg 
                  className="w-6 h-6" 
                  fill="currentColor" 
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zM6 20V4h7v5h5v11H6zm2-8h8v2H8v-2zm0 4h8v2H8v-2z"/>
                </svg>
                <span>{t('questionario.exportPDF', 'Esporta PDF')}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
